import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import type { LandingTopDonor } from '@/lib/landing-top-donors'
import type { LandingTopDonorsPhase } from '@/hooks/useLandingCampaignTopDonors'
import { DonorMascot } from '../DonorMascot'
import { parseMascotConfig, type MascotConfig } from '../mascot-catalog'
import type { MascotGamePayload, SendGameAction, ShopItem } from './mascot-game-types'
import styles from './MascotGame.module.css'
import { MascotCoin } from './MascotCoin'
import { dailyResetIn, gameDay } from './mascot-game-view'
import { ActionCountdown } from './ActionCountdown'
import { MascotCoinShop } from './MascotCoinShop'

type ShopCategory = ShopItem['kind'] | 'coins'
const SHOP_CATEGORIES = [
  ['food', 'Comidinhas'],
  ['care', 'Cuidados'],
  ['garden', 'Jardim'],
  ['decor', 'Decoração'],
  ['coins', 'Comprar moedas'],
] as const

function ShopCategories({
  selected,
  onSelect,
}: {
  selected: ShopCategory
  onSelect: (category: ShopCategory) => void
}) {
  const track = useRef<HTMLDivElement>(null)
  const [navigation, setNavigation] = useState({
    overflow: false,
    left: false,
    right: false,
  })
  const updateNavigation = useCallback(() => {
    const element = track.current
    if (!element) return
    const overflow = element.scrollWidth > element.clientWidth + 2
    setNavigation({
      overflow,
      left: overflow && element.scrollLeft > 2,
      right:
        overflow && element.scrollLeft + element.clientWidth < element.scrollWidth - 2,
    })
  }, [])

  useEffect(() => {
    const element = track.current
    if (!element) return
    const observer = new ResizeObserver(updateNavigation)
    observer.observe(element)
    for (const child of element.children) observer.observe(child)
    updateNavigation()
    return () => observer.disconnect()
  }, [updateNavigation])

  useLayoutEffect(() => {
    const element = track.current
    const active = element?.querySelector<HTMLButtonElement>('[aria-pressed="true"]')
    if (!element || !active) return
    const viewport = element.getBoundingClientRect()
    const button = active.getBoundingClientRect()
    const distance =
      button.left < viewport.left
        ? button.left - viewport.left
        : button.right > viewport.right
          ? button.right - viewport.right
          : 0
    if (distance) element.scrollBy({ left: distance, behavior: 'instant' })
    updateNavigation()
  }, [selected, navigation.overflow, updateNavigation])

  function scroll(direction: -1 | 1) {
    const element = track.current
    if (!element) return
    element.scrollBy({
      left: direction * Math.max(120, element.clientWidth * 0.75),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'instant'
        : 'smooth',
    })
  }

  return (
    <div
      className={`${styles.shopCategories} ${navigation.overflow ? styles.shopCategoriesOverflow : ''}`}
    >
      {navigation.overflow && (
        <button
          type="button"
          className={styles.shopTabArrow}
          aria-label="Categorias anteriores"
          disabled={!navigation.left}
          onClick={() => scroll(-1)}
        >
          <Icon icon="mdi:chevron-left" aria-hidden="true" />
        </button>
      )}
      <div
        ref={track}
        className={styles.shopTabs}
        role="group"
        aria-label="Categorias da loja"
        onScroll={updateNavigation}
      >
        {SHOP_CATEGORIES.map(([id, label]) => (
          <button
            type="button"
            key={id}
            aria-pressed={selected === id}
            onClick={() => onSelect(id)}
          >
            {label}
          </button>
        ))}
      </div>
      {navigation.overflow && (
        <button
          type="button"
          className={styles.shopTabArrow}
          aria-label="Próximas categorias"
          disabled={!navigation.right}
          onClick={() => scroll(1)}
        >
          <Icon icon="mdi:chevron-right" aria-hidden="true" />
        </button>
      )}
    </div>
  )
}

export function MascotShop({
  data,
  send,
  disabled,
  onClose,
  initialCategory = 'food',
  onCoinsPurchased,
}: {
  data: MascotGamePayload
  send: SendGameAction
  disabled: boolean
  onClose: () => void
  initialCategory?: ShopItem['kind'] | 'coins'
  onCoinsPurchased?: () => Promise<void>
}) {
  const [category, setCategory] = useState<ShopItem['kind'] | 'coins'>(initialCategory)
  return (
    <section className={styles.collection} aria-label="Lojinha do mascote">
      <div className={styles.collectionHeading}>
        <div>
          <span className={styles.eyebrow}>COISINHAS BOAS</span>
          <h2>A lojinha da esquina.</h2>
          <p>Planeje suas compras. Novos mimos chegam conforme sua amizade evolui.</p>
        </div>
        <button className={styles.secondaryButton} onClick={onClose}>
          <Icon icon="mdi:arrow-left" />
          Voltar
        </button>
      </div>
      <ShopCategories selected={category} onSelect={setCategory} />
      {category === 'coins' ? (
        <MascotCoinShop
          campaignId={data.campaign_id}
          disabled={disabled}
          onPaid={onCoinsPurchased}
        />
      ) : (
        <>
          <div className={styles.shopGrid}>
            {Object.entries(data.catalog.items)
              .filter(([, item]) => item.kind === category)
              .map(([id, item]) => {
                const owned = item.kind === 'decor' && data.state.owned_decor.includes(id)
                const locked = item.level > data.state.level
                const missingCoins = Math.max(0, item.price - data.state.coins)
                const full = (data.state.inventory[id] ?? 0) >= 99
                return (
                  <article
                    key={id}
                    className={styles.shopItem}
                    data-locked={locked && !owned}
                    data-decor={item.kind === 'decor' ? id : undefined}
                  >
                    <span className={styles.shopArt}>{item.icon}</span>
                    <span className={styles.itemOwned}>
                      {owned
                        ? 'Já é seu'
                        : locked
                          ? `Nível ${item.level}`
                          : `${data.state.inventory[id] ?? 0} na mochila`}
                    </span>
                    <h3>{item.name}</h3>
                    <p>{item.description}</p>
                    {locked && !owned && (
                      <small>
                        {item.price} moedas · faltam {item.level - data.state.level}{' '}
                        níveis
                      </small>
                    )}
                    <button
                      className={styles.secondaryButton}
                      disabled={
                        disabled ||
                        owned ||
                        locked ||
                        data.state.coins < item.price ||
                        (data.state.inventory[id] ?? 0) >= 99
                      }
                      onClick={() => void send({ action: 'buy', item: id })}
                    >
                      {owned || locked ? (
                        <Icon icon={owned ? 'mdi:check' : 'mdi:lock-outline'} />
                      ) : (
                        <MascotCoin className={styles.priceCoin} />
                      )}
                      {owned
                        ? 'Adquirido'
                        : locked
                          ? `Libera no nível ${item.level}`
                          : full
                            ? 'Mochila cheia · máximo 99'
                            : missingCoins > 0
                              ? `Faltam ${missingCoins} moedas`
                              : `${item.price} moedas`}
                    </button>
                  </article>
                )
              })}
          </div>
          <p className={styles.caption}>
            Moedas para uso exclusivo no jogo. Até{' '}
            {data.catalog.rules?.game_coin_limit ?? 400} moedas de minijogos por dia. Na
            nova jornada, é preciso alcançar 50% dos pontos; após 50 moedas no dia, os
            próximos ganhos caem pela metade.
          </p>
        </>
      )}
    </section>
  )
}

export function MascotJournal({
  data,
  send,
  disabled,
  now = data.server_time,
}: {
  data: MascotGamePayload
  send: SendGameAction
  disabled: boolean
  now?: number
}) {
  const { state } = data
  const daily =
    state.day === gameDay(now)
      ? state.daily
      : {
          ...state.daily,
          feed: 0,
          wash: 0,
          play: 0,
          claimed: false,
          game_xp: 0,
          care_xp: 0,
        }
  const rules = data.catalog.rules
  const goals = {
    feed: rules?.mission_feed ?? 1,
    wash: rules?.mission_wash ?? 1,
    play: rules?.mission_play ?? 1,
  }
  const done =
    daily.feed >= goals.feed && daily.wash >= goals.wash && daily.play >= goals.play
  const nextItems = Object.entries(data.catalog.items)
    .filter(([id, item]) => item.level > state.level && !state.owned_decor.includes(id))
    .sort((a, b) => a[1].level - b[1].level)
    .slice(0, 3)
  return (
    <section className={styles.collection} aria-label="Diário do mascote">
      <div className={styles.progressJourney}>
        <span className={styles.eyebrow}>SUA JORNADA · NÍVEL {state.level}</span>
        <h2>Uma amizade que cresce.</h2>
        <p>
          {state.level === 50
            ? 'Você chegou ao nível máximo!'
            : `${state.progress?.current ?? state.xp % 100} / ${state.progress?.required ?? 100} XP para o próximo nível.`}{' '}
          Os cuidados mostram o bem-estar de agora; a experiência abre novas conquistas.
        </p>
        <progress
          aria-label="Jornada para o próximo nível"
          max={100}
          value={state.progress?.percent ?? state.xp % 100}
        />
        <div className={styles.unlocks}>
          {nextItems.map(([id, item]) => (
            <article key={id}>
              <span>{item.icon}</span>
              <small>NÍVEL {item.level}</small>
              <strong>{item.name}</strong>
              <small>{item.price} moedas</small>
            </article>
          ))}
        </div>
        {rules && (
          <p className={styles.caption}>
            Hoje: {daily.game_xp ?? 0}/{rules.game_xp_limit} XP nos jogos ·{' '}
            {daily.care_xp ?? 0}/{rules.care_xp_limit} XP nos cuidados. Carinhos dão XP
            nas três primeiras vezes do dia. Dificuldade atual:{' '}
            {data.catalog.challenge?.name}.
          </p>
        )}
      </div>
      <div className={styles.missionCard}>
        <div>
          <span className={styles.eyebrow}>UM POUQUINHO TODOS OS DIAS</span>
          <h2>Hoje a missão é cuidar.</h2>
          <p>
            Complete os objetivos para ganhar {rules?.mission_coins ?? 40} moedas e{' '}
            {rules?.mission_xp ?? 20} XP.
          </p>
        </div>
        <div className={styles.missionSteps}>
          {(
            [
              ['feed', 'Servir refeições'],
              ['wash', 'Dar um banho'],
              ['play', 'Alcançar 50% dos pontos em partidas'],
            ] as const
          ).map(([id, title]) => (
            <div key={id} data-done={daily[id] >= goals[id]}>
              <Icon
                icon={daily[id] >= goals[id] ? 'mdi:check-circle' : 'mdi:circle-outline'}
              />
              {title} · {Math.min(daily[id], goals[id])}/{goals[id]}
            </div>
          ))}
        </div>
        <button
          className={styles.primaryButton}
          disabled={disabled || !done || daily.claimed}
          onClick={() => void send({ action: 'mission' })}
        >
          <Icon icon="mdi:gift-outline" />
          <span className={styles.actionCopy}>
            <span>{daily.claimed ? 'Recompensa recebida' : 'Receber recompensa'}</span>
            {daily.claimed ? (
              <ActionCountdown seconds={dailyResetIn(now)} label="Nova missão em" />
            ) : (
              !done && (
                <small className={styles.actionHint}>Complete os objetivos acima</small>
              )
            )}
          </span>
        </button>
        <small>As missões renovam à meia-noite, no horário de Brasília.</small>
      </div>
      <div className={styles.journalStats}>
        <span>
          <strong>{state.stats.feeds}</strong>refeições
        </span>
        <span>
          <strong>{state.stats.games}</strong>partidas pontuadas
        </span>
        <span>
          <strong>{state.streak}</strong>dias de presentes
        </span>
      </div>
      <div className={styles.collectionHeading}>
        <div>
          <span className={styles.eyebrow}>NOSSA COLEÇÃO</span>
          <h2>Memórias que ficam.</h2>
          <p>Cada selo conquistado vale mais {rules?.achievement_coins ?? 25} moedas.</p>
        </div>
        <span className={styles.countPill}>
          {state.achievements.length}/{Object.keys(data.catalog.achievements).length}
        </span>
      </div>
      <div className={styles.badgeGrid}>
        {Object.entries(data.catalog.achievements).map(([id, badge]) => {
          const unlocked = state.achievements.includes(id)
          const value =
            badge.stat === 'level' ? state.level : (state.stats[badge.stat] ?? 0)
          return (
            <article
              key={id}
              className={`${styles.badge} ${unlocked ? styles.badgeUnlocked : ''}`}
            >
              <span>{badge.icon}</span>
              <h3>{badge.name}</h3>
              <p>{badge.description}</p>
              <small>
                {unlocked
                  ? 'Conquistado!'
                  : `${Math.min(value, badge.target)} de ${badge.target}`}
              </small>
              <progress
                max={badge.target}
                value={value}
                aria-label={`Progresso de ${badge.name}`}
              />
            </article>
          )
        })}
      </div>
    </section>
  )
}

export function MascotCommunity({
  items,
  phase,
  onRetry,
}: {
  items: LandingTopDonor[]
  phase: LandingTopDonorsPhase
  onRetry: () => void
}) {
  const [visitor, setVisitor] = useState<{ config: MascotConfig; owner: string } | null>(
    null
  )
  const companions = items.flatMap((item) => {
    const config = parseMascotConfig(item.mascot)
    return config ? [{ config, owner: item.display_name, rank: item.rank }] : []
  })
  return (
    <section className={styles.collection} aria-label="Vizinhança da campanha">
      {visitor ? (
        <div className={styles.visit}>
          <span className={styles.eyebrow}>VISITINHA ESPECIAL</span>
          <DonorMascot config={visitor.config} />
          <h2>Olá! Eu sou {visitor.config.name || 'um novo amigo'}.</h2>
          <p>Companheiro de {visitor.owner} nesta campanha.</p>
          <button className={styles.primaryButton} onClick={() => setVisitor(null)}>
            Voltar para a turma
          </button>
        </div>
      ) : (
        <>
          <div className={styles.collectionHeading}>
            <div>
              <span className={styles.eyebrow}>MASCOTES DA CAMPANHA</span>
              <h2>Apoiar fica melhor em companhia.</h2>
              <p>Os companheiros dos participantes que aparecem no ranking.</p>
            </div>
          </div>
          {phase === 'loading' ? (
            <p role="status">Conhecendo a turma…</p>
          ) : phase === 'error' ? (
            <div>
              <p role="alert">Não foi possível carregar a turma.</p>
              <button className={styles.secondaryButton} onClick={onRetry}>
                Tentar novamente
              </button>
            </div>
          ) : companions.length === 0 ? (
            <div className={styles.emptyCommunity}>
              <span>🐾</span>
              <h3>A turma está chegando.</h3>
              <p>Os mascotes criados pelos maiores compradores aparecem aqui.</p>
            </div>
          ) : (
            <div className={styles.communityGrid}>
              {companions.map((item) => (
                <button key={item.rank} onClick={() => setVisitor(item)}>
                  <DonorMascot config={item.config} />
                  <strong>{item.config.name || 'Companheiro'}</strong>
                  <span>{item.owner}</span>
                  <small>
                    Fazer uma visita <Icon icon="mdi:arrow-right" />
                  </small>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  )
}
